import type {
  CaseQuery,
  CaseStatus,
  CaseSummary,
  DashboardStats,
  EmailDraft,
  PageInfo,
  PortalConfig,
  PortalUser,
  RoleInfo,
  UserRole,
  ViolationCase,
} from './types'

export class ApiError extends Error {
  status: number
  problems?: string[]

  constructor(message: string, status: number, problems?: string[]) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.problems = problems
  }
}

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const controller = new AbortController()
  const timer = window.setTimeout(() => controller.abort(), 20000)
  let response: Response
  try {
    response = await fetch(url, {
      credentials: 'same-origin',
      ...init,
      signal: init?.signal ?? controller.signal,
      headers:
        init?.body instanceof FormData
          ? init?.headers
          : { 'Content-Type': 'application/json', ...(init?.headers || {}) },
    })
  } catch (cause) {
    window.clearTimeout(timer)
    if (cause instanceof DOMException && cause.name === 'AbortError') {
      throw new ApiError('The portal took too long to respond. Refresh and try again.', 0)
    }
    throw cause
  }
  window.clearTimeout(timer)

  if (!response.ok) {
    let message = `Request failed (${response.status})`
    let problems: string[] | undefined
    try {
      const payload = await response.json()
      message = payload.message || payload.error || message
      problems = payload.problems
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(message, response.status, problems)
  }

  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

export const api = {
  config: () => request<PortalConfig>('/api/config'),

  login: (email: string, password: string) =>
    request<{ user: PortalUser }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  logout: () => request<{ ok: boolean }>('/api/auth/logout', { method: 'POST' }),

  me: () => request<{ user: PortalUser | null }>('/api/auth/me'),

  listUsers: () =>
    request<{
      users: PortalUser[]
      roles: RoleInfo[]
      totals: {
        accounts: number
        active: number
        opened: number
        handled: number
        submitted: number
      }
    }>('/api/users'),

  recordCaseView: (idOrNumber: string) =>
    request<{ ok: boolean; recorded?: boolean; case?: ViolationCase }>(
      `/api/cases/${encodeURIComponent(idOrNumber)}/view`,
      { method: 'POST' },
    ),

  createUser: (payload: {
    name: string
    email: string
    role: UserRole
    password: string
  }) =>
    request<{ user: PortalUser }>('/api/users', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  updateUser: (
    id: string,
    payload: Partial<{
      name: string
      email: string
      role: UserRole
      password: string
      active: boolean
    }>,
  ) =>
    request<{ user: PortalUser }>(`/api/users/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  deleteUser: (id: string) =>
    request<{ deleted: boolean }>(`/api/users/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  uploadAvatar: (id: string, file: File) => {
    const form = new FormData()
    form.append('avatar', file)
    return request<{ user: PortalUser }>(`/api/users/${encodeURIComponent(id)}/avatar`, {
      method: 'POST',
      body: form,
    })
  },

  removeAvatar: (id: string) =>
    request<{ user: PortalUser }>(`/api/users/${encodeURIComponent(id)}/avatar`, {
      method: 'DELETE',
    }),

  /** @deprecated use logout */
  lock: () => request<{ unlocked: boolean }>('/api/lock', { method: 'POST' }),

  listCases: (params: CaseQuery = {}) => {
    const query = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null || value === '' || value === 'all') continue
      query.set(key, String(value))
    }
    const suffix = query.toString() ? `?${query}` : ''
    return request<{ cases: CaseSummary[]; page: PageInfo; stats: DashboardStats }>(
      `/api/cases${suffix}`,
    )
  },

  createCase: (payload: Partial<ViolationCase> = {}) =>
    request<{ case: ViolationCase; url: string }>('/api/cases', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  getCase: (idOrNumber: string) =>
    request<{ case: ViolationCase; url: string }>(`/api/cases/${encodeURIComponent(idOrNumber)}`),

  updateCase: (idOrNumber: string, patch: Record<string, unknown>) =>
    request<{ case: ViolationCase; url: string }>(`/api/cases/${encodeURIComponent(idOrNumber)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  submitCase: (idOrNumber: string, patch: Record<string, unknown> = {}) =>
    request<{ case: ViolationCase; url: string }>(
      `/api/cases/${encodeURIComponent(idOrNumber)}/submit`,
      { method: 'POST', body: JSON.stringify(patch) },
    ),

  setStatus: (idOrNumber: string, status: CaseStatus, note = '') =>
    request<{ case: ViolationCase }>(`/api/cases/${encodeURIComponent(idOrNumber)}/status`, {
      method: 'POST',
      body: JSON.stringify({ status, note }),
    }),

  addNote: (idOrNumber: string, note: string, actor = 'Reporter') =>
    request<{ case: ViolationCase }>(`/api/cases/${encodeURIComponent(idOrNumber)}/note`, {
      method: 'POST',
      body: JSON.stringify({ note, actor }),
    }),

  deleteCase: (idOrNumber: string) =>
    request<{ deleted: boolean }>(`/api/cases/${encodeURIComponent(idOrNumber)}`, {
      method: 'DELETE',
    }),

  deleteEvidence: (idOrNumber: string, evidenceId: string) =>
    request<{ case: ViolationCase }>(
      `/api/cases/${encodeURIComponent(idOrNumber)}/evidence/${evidenceId}`,
      { method: 'DELETE' },
    ),

  emailDraft: (idOrNumber: string) =>
    request<{ draft: EmailDraft; mailto: string; smtpEnabled: boolean; url: string }>(
      `/api/cases/${encodeURIComponent(idOrNumber)}/email/draft`,
    ),

  sendEmail: (
    idOrNumber: string,
    payload: {
      to?: string[] | string
      cc?: string[] | string
      subject?: string
      text?: string
      markSentOnly?: boolean
    },
  ) =>
    request<{
      case: ViolationCase
      delivered: boolean
      reason?: string
      mailto?: string
      draft?: EmailDraft
    }>(`/api/cases/${encodeURIComponent(idOrNumber)}/email`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
}

/** Uploads with progress, which fetch cannot report. */
export function uploadEvidence(
  idOrNumber: string,
  files: File[],
  onProgress?: (fraction: number) => void,
): Promise<{ case: ViolationCase }> {
  return new Promise((resolve, reject) => {
    const form = new FormData()
    for (const file of files) form.append('files', file)

    const xhr = new XMLHttpRequest()
    xhr.open('POST', `/api/cases/${encodeURIComponent(idOrNumber)}/evidence`)

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) onProgress(event.loaded / event.total)
    })

    xhr.addEventListener('load', () => {
      try {
        const payload = JSON.parse(xhr.responseText || '{}')
        if (xhr.status >= 200 && xhr.status < 300) resolve(payload)
        else reject(new ApiError(payload.message || 'Upload failed', xhr.status))
      } catch {
        reject(new ApiError('Upload failed: unreadable server response', xhr.status))
      }
    })

    xhr.addEventListener('error', () => reject(new ApiError('Upload failed: network error', 0)))
    xhr.addEventListener('abort', () => reject(new ApiError('Upload cancelled', 0)))

    xhr.send(form)
  })
}

export const mediaUrl = (caseId: string, file: string) =>
  `/media/${caseId}/${encodeURIComponent(file)}`

export const avatarUrl = (file: string | null | undefined) =>
  file ? `/avatars/${encodeURIComponent(file)}` : null
